// Servicios CRUD para buyer_profiles con tracking en engagement_events
// Incluye cálculo automático de fit_percentage al crear/actualizar

import { supabase } from '../../supabaseClient';
import { createEngagementEvent } from './engagement';
import { calculateAndSaveFit } from '../propertyFit/calculateBuyerFit';

/**
 * Obtener perfil de comprador por propiedad y token
 * @param {string} propertyId - ID de la propiedad
 * @param {string} inviteToken - Token de invitación del comprador
 * @returns {Object|null} Perfil del comprador o null si no existe
 */
export const getBuyerProfile = async (propertyId, inviteToken) => {
  try {
    // Usar order + limit en lugar de maybeSingle para manejar duplicados
    // Si hay duplicados, toma el más reciente
    const { data, error } = await supabase
      .from('buyer_profiles')
      .select('*')
      .eq('property_id', propertyId)
      .eq('invite_token', inviteToken)
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) throw error;

    if (!data || data.length === 0) {
      console.log('📋 No existe perfil para este comprador');
      return null;
    }

    return mapBuyerProfileData(data[0]);
  } catch (error) {
    console.error('Error obteniendo perfil de comprador:', error);
    throw error;
  }
};

/**
 * Crear nuevo perfil de comprador
 * @param {Object} profileData - Datos del perfil
 * @returns {Object} Perfil creado
 */
export const createBuyerProfile = async (profileData) => {
  try {
    const insertData = {
      property_id: profileData.propertyId,
      invite_token: profileData.inviteToken,
      user_email: profileData.userEmail,
      payment_method: profileData.paymentMethod,
      financing_status: profileData.financingStatus,
      property_to_sell: profileData.propertyToSell,
      min_bedrooms: profileData.minBedrooms || null,
      min_bathrooms: profileData.minBathrooms || null,
      desired_sqm: profileData.desiredSqm || null,
      outdoor_space: profileData.outdoorSpace || null,
      reform_level: profileData.reformLevel || null,
      elevator: profileData.elevator || null,
      parking: profileData.parking || null,
      additional_comments: profileData.additionalComments || null,
    };

    // Usar upsert para evitar duplicados - actualiza si ya existe
    const { data, error } = await supabase
      .from('buyer_profiles')
      .upsert([insertData], {
        onConflict: 'property_id,invite_token',
        ignoreDuplicates: false
      })
      .select()
      .single();

    if (error) throw error;

    // Registrar evento en engagement_events
    await createEngagementEvent(
      profileData.propertyId,
      profileData.inviteToken,
      profileData.sessionId || generateSessionId(),
      'profile_created',
      {
        profile_id: data.id,
        payment_method: profileData.paymentMethod,
        financing_status: profileData.financingStatus,
        property_to_sell: profileData.propertyToSell,
        has_features: !!(profileData.minBedrooms || profileData.minBathrooms || profileData.desiredSqm),
        timestamp: new Date().toISOString(),
      },
      profileData.metadata || {}
    );

    console.log('✅ Perfil de comprador creado:', data.id);

    // Calcular y guardar fit_percentage (async, no bloquea)
    calculateAndSaveFit(data.id, profileData, profileData.propertyId)
      .catch(err => console.error('Error calculando fit:', err));

    return mapBuyerProfileData(data);
  } catch (error) {
    console.error('Error creando perfil de comprador:', error);
    throw error;
  }
};

/**
 * Actualizar perfil de comprador existente
 * @param {string} profileId - ID del perfil
 * @param {Object} updates - Campos a actualizar
 * @param {Object} context - Contexto para tracking (propertyId, inviteToken, etc)
 * @returns {Object} Perfil actualizado
 */
export const updateBuyerProfile = async (profileId, updates, context = {}) => {
  try {
    // Primero obtener el perfil actual para comparar cambios
    const { data: currentProfile, error: fetchError } = await supabase
      .from('buyer_profiles')
      .select('*')
      .eq('id', profileId)
      .single();

    if (fetchError) throw fetchError;

    // Preparar datos de actualización
    const updateData = {};
    if (updates.paymentMethod !== undefined) updateData.payment_method = updates.paymentMethod;
    if (updates.financingStatus !== undefined) updateData.financing_status = updates.financingStatus;
    if (updates.propertyToSell !== undefined) updateData.property_to_sell = updates.propertyToSell;
    if (updates.minBedrooms !== undefined) updateData.min_bedrooms = updates.minBedrooms;
    if (updates.minBathrooms !== undefined) updateData.min_bathrooms = updates.minBathrooms;
    if (updates.desiredSqm !== undefined) updateData.desired_sqm = updates.desiredSqm;
    if (updates.outdoorSpace !== undefined) updateData.outdoor_space = updates.outdoorSpace;
    if (updates.reformLevel !== undefined) updateData.reform_level = updates.reformLevel;
    if (updates.elevator !== undefined) updateData.elevator = updates.elevator;
    if (updates.parking !== undefined) updateData.parking = updates.parking;
    if (updates.additionalComments !== undefined) updateData.additional_comments = updates.additionalComments;

    // Actualizar en BD
    const { data, error } = await supabase
      .from('buyer_profiles')
      .update(updateData)
      .eq('id', profileId)
      .select()
      .single();

    if (error) throw error;

    // Registrar cambios en engagement_events
    const changes = detectChanges(currentProfile, data);
    
    if (changes.length > 0 && context.propertyId && context.inviteToken) {
      await createEngagementEvent(
        context.propertyId,
        context.inviteToken,
        context.sessionId || generateSessionId(),
        'profile_updated',
        {
          profile_id: profileId,
          changes: changes,
          updated_fields: changes.map(c => c.field),
          timestamp: new Date().toISOString(),
        },
        context.metadata || {}
      );
    }

    console.log('✅ Perfil actualizado:', profileId, `(${changes.length} cambios)`);

    // Recalcular fit_percentage si cambiaron campos relevantes (async, no bloquea)
    const fitRelevantFields = ['minBedrooms', 'minBathrooms', 'desiredSqm', 'elevator', 'parking', 'outdoorSpace'];
    const fitFieldsChanged = changes.some(c => fitRelevantFields.includes(c.field));

    if (fitFieldsChanged && context.propertyId) {
      const updatedProfile = mapBuyerProfileData(data);
      calculateAndSaveFit(profileId, updatedProfile, context.propertyId)
        .catch(err => console.error('Error recalculando fit:', err));
    }

    return mapBuyerProfileData(data);
  } catch (error) {
    console.error('Error actualizando perfil:', error);
    throw error;
  }
};

/**
 * Obtener todos los perfiles de una propiedad (para admin)
 * @param {string} propertyId - ID de la propiedad
 * @returns {Array} Lista de perfiles
 */
export const getPropertyBuyerProfiles = async (propertyId) => {
  try {
    const { data, error } = await supabase
      .from('buyer_profiles')
      .select('*')
      .eq('property_id', propertyId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    return data.map(mapBuyerProfileData);
  } catch (error) {
    console.error('Error obteniendo perfiles de propiedad:', error);
    throw error;
  }
};

/**
 * Verificar si un comprador tiene perfil completo
 * @param {string} propertyId - ID de la propiedad
 * @param {string} inviteToken - Token de invitación
 * @returns {boolean} true si tiene perfil, false si no
 */
export const hasCompletedProfile = async (propertyId, inviteToken) => {
  try {
    const profile = await getBuyerProfile(propertyId, inviteToken);
    return profile !== null;
  } catch (error) {
    console.error('Error verificando perfil:', error);
    return false;
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Mapear datos de BD (snake_case) a frontend (camelCase)
 */
const mapBuyerProfileData = (data) => {
  if (!data) return null;

  return {
    id: data.id,
    propertyId: data.property_id,
    inviteToken: data.invite_token,
    userEmail: data.user_email,
    paymentMethod: data.payment_method,
    financingStatus: data.financing_status,
    propertyToSell: data.property_to_sell,
    minBedrooms: data.min_bedrooms,
    minBathrooms: data.min_bathrooms,
    desiredSqm: data.desired_sqm,
    outdoorSpace: data.outdoor_space,
    reformLevel: data.reform_level,
    elevator: data.elevator,
    parking: data.parking,
    additionalComments: data.additional_comments,
    fitPercentage: data.fit_percentage, // % de compatibilidad con la propiedad
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
};

/**
 * Detectar cambios entre perfil antiguo y nuevo
 */
const detectChanges = (oldProfile, newProfile) => {
  const changes = [];
  const fieldMap = {
    payment_method: 'paymentMethod',
    financing_status: 'financingStatus',
    property_to_sell: 'propertyToSell',
    min_bedrooms: 'minBedrooms',
    min_bathrooms: 'minBathrooms',
    desired_sqm: 'desiredSqm',
    outdoor_space: 'outdoorSpace',
    reform_level: 'reformLevel',
    elevator: 'elevator',
    parking: 'parking',
    additional_comments: 'additionalComments',
  };

  for (const [dbField, frontendField] of Object.entries(fieldMap)) {
    if (oldProfile[dbField] !== newProfile[dbField]) {
      changes.push({
        field: frontendField,
        oldValue: oldProfile[dbField],
        newValue: newProfile[dbField],
      });
    }
  }

  return changes;
};

/**
 * Generar session ID único
 */
const generateSessionId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};